import React from 'react';
import { useMinViewport } from '@/hooks/common/use-min-viewport';
import { TourCardCompact } from './TourCardCompact';
import { TourCardSplit } from './TourCardSplit';
import { resolveMedia, type ResolvedFrame, type TourMedia, type TourStep } from '../types';

/**
 * Minimum viewport width (px) for the split-panel layout. Below this the
 * dispatcher falls back to the compact card with the same step content.
 * Picked to match RESEARCH §3 Variant 2 ("640×~280px wide cards anchored
 * to a target near the right edge of /inventory should not flip + overlap
 * page content"). Reacts to live resize via matchMedia.
 */
const SPLIT_LAYOUT_MIN_VIEWPORT_PX = 1280;

interface Props {
  step: TourStep;
  frame: ResolvedFrame;
  stepNumber: number;
  totalSteps: number;
  frameNumber: number;
  frameCount: number;
  targetEl: HTMLElement | null;
  secondaryEl?: HTMLElement | null;
  isFirst: boolean;
  isLast: boolean;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
  /** Stops the tour without persisting seen_version. Optional — when
   * undefined or when the per-version reminder count has reached its
   * limit, the affordance is hidden. */
  onRemindLater?: () => void;
  /** When true, hide the "Remind me later" button. Used by TourProvider
   * to enforce the per-version reminder limit. */
  hideRemindLater?: boolean;
  /** Resolved media for the next position in the tour, used for lazy
   * preload of upcoming video assets. Optional — if absent, no preload. */
  nextMedia?: TourMedia;
  /** Jump-to-frame callback. The TourProvider exposes this; the dispatcher
   * forwards it to the compact / split body components so their stepper
   * dots can wire it up. */
  onGoToFrame?: (targetIndex: number) => void;
}

/**
 * Layout dispatcher. Reads `step.layout` and renders the matching card
 * component. Defaults to `'compact'` when absent. The split-panel branch
 * is wired in Phase 2 (Task 11).
 *
 * Why a dispatcher: both compact and split share the same chrome
 * (progress bar / focus trap / action row) but lay out their bodies
 * differently. Centralising the choice here keeps the consumer
 * (TourProvider) ignorant of the layout system.
 */
export const TourCard: React.FC<Props> = ({
  step,
  frame,
  stepNumber,
  totalSteps,
  frameNumber,
  frameCount,
  targetEl,
  secondaryEl,
  isFirst,
  isLast,
  onNext,
  onBack,
  onSkip,
  onRemindLater,
  hideRemindLater,
  nextMedia,
  onGoToFrame,
}) => {
  // Pull the raw frame back out so resolveMedia can apply its priority
  // ladder (frame.media > step.media > legacy previewImage). The chrome
  // and body components only need the resolved TourMedia value.
  // `frameNumber` is 1-based (UI counter); arrays are 0-based.
  const frameIndex = frameNumber - 1;
  const rawFrame = step.frames?.[frameIndex] ?? null;
  const media = resolveMedia(step, rawFrame);

  const layout = step.layout ?? 'compact';
  // Live viewport gate. The dispatcher re-renders when matchMedia fires,
  // so a user resizing into / out of 1280px sees the layout flip without
  // having to advance the tour.
  const wideEnough = useMinViewport(SPLIT_LAYOUT_MIN_VIEWPORT_PX);
  const Layout =
    layout === 'split' && wideEnough ? TourCardSplit : TourCardCompact;

  return (
    <Layout
      step={step}
      frame={frame}
      media={media}
      preloadNext={nextMedia}
      stepNumber={stepNumber}
      totalSteps={totalSteps}
      frameNumber={frameNumber}
      frameCount={frameCount}
      targetEl={targetEl}
      secondaryEl={secondaryEl}
      isFirst={isFirst}
      isLast={isLast}
      onNext={onNext}
      onBack={onBack}
      onSkip={onSkip}
      onRemindLater={onRemindLater}
      hideRemindLater={hideRemindLater}
      onGoToFrame={onGoToFrame}
    />
  );
};
